import { FC, FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAppForm } from '@new/common/hooks/form';
import { formOptions } from '@tanstack/react-form';
import z from 'zod';
import { useQuery } from '@utils/routerHooks';
import { useResetPasswordMutation } from '@teachur/http-client';
import { getErrorMessage } from '@new/lib';
import { toast } from 'sonner';
import { passwordValidation } from '../../schemas/validations';

const resetSchema = z
  .object({
    password: passwordValidation,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

const resetFormOpts = formOptions({
  defaultValues: {
    password: '',
    confirmPassword: '',
  },
});

export const ResetPasswordForm: FC = () => {
  const navigate = useNavigate();
  const query = useQuery<{ token: string }>();
  const { mutateAsync: resetPassword } = useResetPasswordMutation({
    onSuccess: () => {
      toast.success('Password reset successfully');
      navigate({ to: '/login' });
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Failed to reset password');
      toast.error(message);
    },
  });
  const form = useAppForm({
    ...resetFormOpts,
    validators: {
      onChange: ({ value }) => {
        const parsed = resetSchema.safeParse(value);
        if (parsed.success) return { fields: {} };

        const errors: Record<string, string> = {};

        parsed.error.issues.forEach((issue) => {
          const field = issue.path[0];
          if (typeof field === 'string') errors[field] = issue.message;
        });

        return { fields: errors };
      },
    },
    onSubmit: async ({ value }) => {
      const parsed = resetSchema.safeParse(value);
      if (!parsed.success) {
        return;
      }
      try {
        await resetPassword({ token: query.token, password: parsed.data.password });
      } catch (error) {
        void error;
      }
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <>
      <div className="w-full flex flex-col justify-between items-center gap-10">
        <div className="flex flex-col justify-center items-center gap-1">
          <h1 className="[font-family:var(--font-arvo)]! [font-size:var(--fs-h3)]! md:[font-size:var(--fs-h2)]! xl:[font-size:var(--fs-h1)]! leading-(--lh-h)! font-bold! m-0! text-(--neutral-black-NB1000)!">
            Create new password
          </h1>
          <p className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! text-center! leading-(--lh-p)! font-medium! m-0! text-(--neutral-black-NB900)!">
            Please enter details below to create new password.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="w-full flex flex-col gap-3"
          data-test-id="reset-password-form"
        >
          <form.AppField
            name="password"
            children={(field) => (
              <field.Password label="New password" placeholder="Enter your new password" />
            )}
          />
          <form.AppField
            name="confirmPassword"
            children={(field) => (
              <field.Password
                label="Confirm new password"
                placeholder="Confirm your new password"
              />
            )}
          />

          <form.AppForm>
            <form.SubmitButton label="Reset password" />
          </form.AppForm>
        </form>
      </div>

      <div className="flex items-center justify-center ">
        <Link
          to="/login"
          className="font-size:var(--fs-b4) lg:[font-size:var(--fs-b3)] [&&]:[font-family:var(--font-arvo)]! bold underline! text-(--primary-O900)!"
        >
          Back to login
        </Link>
      </div>
    </>
  );
};
